import * as ReactDOM from "react-dom/client";
import * as React from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Terms } from "./payments/Terms";
import PaymentButton from "../src/UI/PaymentButton";
import Alert from "../src/UI/Alerts/Alert";
import { useTermsAgreed } from "./payments/useTermsAgreed";
import { handleStripeConfirmError } from "./payments/handleStripeConfirmError";
import { i18n } from "../src/i18n";

document.addEventListener("turbo:load", init);
document.addEventListener("turbo:frame-load", init);
document.addEventListener("turbo:before-cache", resetMountedState);

function resetMountedState() {
  const node = document.querySelector("#consultationPayment");

  if (!node) {
    return;
  }

  delete (node as HTMLElement).dataset.reactMounted;
}

function init(event) {
  const node = event.target.querySelector("#consultationPayment");

  if (!node) {
    return;
  }

  if (node.dataset.reactMounted === "true") {
    return;
  }

  node.dataset.reactMounted = "true";

  const stripePromise = loadStripe(node.dataset.stripePublishableKey);
  const clientSecret = node.dataset.stripeClientSecret;
  const returnUrl = node.dataset.returnUrl;
  const cost = node.dataset.cost;
  const options = { clientSecret };
  const root = ReactDOM.createRoot(node);

  root.render(
    <>
      <Elements options={options} stripe={stripePromise}>
        <CheckoutForm returnUrl={returnUrl} cost={cost} />
      </Elements>
    </>
  );
}

interface CheckoutFormProps {
  returnUrl: string;
  cost: number;
}

function CheckoutForm({ returnUrl, cost }: CheckoutFormProps) {
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const terms = useTermsAgreed();

  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    if (!terms.allAgreed || !stripe || !elements) {
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    });

    handleStripeConfirmError(error, setError);

    setIsLoading(false);
  };

  return (
    <form id="payment-form" className="mb-4" onSubmit={handleSubmit}>
      <PaymentElement id="payment-element" />
      <div className="mb-4 mt-6">
        <Terms
          id="stripe"
          onTermsChange={terms.setConditions}
          onPolicyChange={terms.setPrivacyPolicy}
        />
      </div>

      <PaymentButton
        id="submit"
        className="mb-4"
        loading={isLoading || !stripe || !elements}
        disabled={!terms.allAgreed}
      >
        Pay {i18n.localize("currency", cost)}
      </PaymentButton>

      {error && <Alert type="error">{error}</Alert>}
    </form>
  );
}
